import { Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
@Component({
    selector: 'gtw-messages',
    templateUrl: './messages.component.html',
    styleUrls: ['./messages.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MessagesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  baseURLs: any = {};
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  title = 'Messages';
  comments: any[] = [];
  filteredComments: any[] = [];
  selectedComment: any = null;
  searchText = '';
  messageType = '1';
  showFormLoader = true;
  contactOpen = false;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;
    this.loadComments();
    this.cdr.detectChanges();
  }

  onRefresh(): void {
    this.loadComments();
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  onFilterChange(): void {
    this.applyFilter();
  }

  onSearch(): void {
    this.applyFilter();
  }

  selectComment(comment: any): void {
    if (!comment?.comment_key) {
      this.selectedComment = comment;
      return;
    }
    const params: any = {
      comment_key: comment.comment_key,
      status: this.messageType === '6' ? 'closed' : 'open'
    };
    this.postForm('/commentByKey', params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.selectedComment = data?.jsonObject || comment;
        this.cdr.detectChanges();
      },
      () => {
        this.selectedComment = comment;
        this.cdr.detectChanges();
      }
    );
  }

  commentReply(event?: KeyboardEvent): void {
    if (event && event.key !== 'Enter') return;
  }

  private loadComments(): void {
    this.showFormLoader = true;
    const status = this.messageType === '6' ? 'closed' : 'open';
    this.postForm('/loadComments', { status }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.comments = Array.isArray(data?.jsonObject) ? data.jsonObject : [];
        this.applyFilter();
        this.showFormLoader = false;
        if (this.filteredComments.length > 0) {
          this.selectComment(this.filteredComments[0]);
        }
        this.cdr.detectChanges();
      },
      () => {
        this.comments = [];
        this.filteredComments = [];
        this.selectedComment = null;
        this.showFormLoader = false;
        this.cdr.detectChanges();
      }
    );
  }

  private applyFilter(): void {
    const txt = (this.searchText || '').toLowerCase();
    this.filteredComments = (this.comments || []).filter((c: any) => {
      const msg = String(c?.message || '').toLowerCase();
      const sub = String(c?.subject || '').toLowerCase();
      const dn = String(c?.display_name || '').toLowerCase();
      return !txt || msg.includes(txt) || sub.includes(txt) || dn.includes(txt);
    });
  }

  private postForm(endpoint: string, params: any) {
    const body = new URLSearchParams();
    Object.keys(params || {}).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });
    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post<any>(`${this.GENERAL_CONFIG.base_url}${endpoint}`, body.toString(), { headers, withCredentials: true });
  }
}
